import { useCallback, useEffect, useMemo, useState } from 'react';
import { FieldValues, useForm } from 'react-hook-form';
import { Grid2, Paper, Stack, Typography } from '@mui/material';
import { FormField, FormFieldType } from '@enroller/admissions-components';

import { useHeaderContext, ValidationType } from '../../../contexts/HeaderContext';
import { usePageTitle } from '@enroller/utils';
import { yesNoOptions } from '../../../constants/yesNoOptions';
import { CommunityFormValues, communitySchema, defaultValues } from './CommunityFormValues';
import { zodResolver } from '@hookform/resolvers/zod';

const MAX_CHARACTERS_DESCRIPTION = 1000;

const schoolTransportOptions = [{ value: 'Car' }, { value: 'School Bus' }, { value: 'Public Transportation' }, { value: 'Walk' }];

interface CommunityFormProps {
  applicationData?: FieldValues;
  submitApplication: (data: FieldValues) => void;
}

export const CommunityForm = ({ applicationData, submitApplication }: CommunityFormProps) => {
  const { setSubmit, setHeaderTitle, setIsFormLoading } = useHeaderContext();
  const { setPageTitle } = usePageTitle();
  const [validationMode, setValidationMode] = useState<ValidationType | ''>('');

  useEffect(() => {
    setHeaderTitle({ title: 'Community', subtitle: '' });
    setPageTitle('Community');
  }, [setHeaderTitle, setPageTitle]);

  // Define resolver based on validation mode needed
  const resolver = useMemo(() => zodResolver(validationMode === ValidationType.Full ? communitySchema.required : communitySchema.optional), [validationMode]);

  const { control, handleSubmit, reset, watch } = useForm<CommunityFormValues>({ defaultValues, resolver });

  useEffect(() => {
    if (applicationData) {
      reset({
        ...defaultValues,
        ...applicationData,
      });
    }
  }, [applicationData, reset]);

  const onSubmit = useCallback(
    () =>
      handleSubmit((data) => {
        // Format data
        console.log('Form submitted: ', data);
        setIsFormLoading(true);
        submitApplication(data);

        setIsFormLoading(false);
      })(),
    [handleSubmit, setIsFormLoading, submitApplication],
  );

  // Submit form when resolver is changed.
  useEffect(() => {
    if (validationMode) {
      onSubmit();
      setValidationMode('');
    }
  }, [resolver, validationMode, onSubmit]);

  useEffect(() => {
    setSubmit(setValidationMode);
  }, [setSubmit, setValidationMode]);

  const watchSpecialTransportation = watch('schoolFamilyInfo.specialTransportation.value');

  return (
    <Stack component="form" onSubmit={onSubmit} gap={2}>
      <Paper sx={{ p: 2 }}>
        <Grid2 container spacing={3}>
          <Grid2 size={12}>
            <Typography variant="subtitle1">Community</Typography>
          </Grid2>

          <Grid2 size={4}>
            <FormField type={FormFieldType.Text} label="Population of your city/town" name="communitySection.cityPopulation" control={control} />
          </Grid2>
          <Grid2 size={4}>
            <FormField type={FormFieldType.Text} label="City or town website" name="communitySection.cityWebsite" control={control} />
          </Grid2>
          <Grid2 size={4}>
            <FormField type={FormFieldType.Text} label="Region of U.S." name="communitySection.region" control={control} />
          </Grid2>
          <Grid2 size={4}>
            <FormField type={FormFieldType.Text} label="Nearest major city" name="communitySection.nearestCity" control={control} />
          </Grid2>
          <Grid2 size={4}>
            <FormField type={FormFieldType.Text} label="Population of the nearest major city" name="communitySection.nearestCityPopulation" control={control} />
          </Grid2>
          <Grid2 size={4}>
            <FormField type={FormFieldType.Text} label="Distance from city" name="communitySection.distanceFromCity" control={control} textSuffix="miles" />
          </Grid2>
          <Grid2 size={12}>
            <FormField
              type={FormFieldType.Text}
              label="Brief description of the community, including history, topography, elevation, landscape, etc."
              name="communitySection.communityDescription"
              control={control}
              multiline
              rows={6}
              maxLength={MAX_CHARACTERS_DESCRIPTION}
            />
          </Grid2>
          <Grid2 size={12}>
            <FormField
              type={FormFieldType.Text}
              label="Places and events of interest"
              name="communitySection.placesAndEvents"
              control={control}
              multiline
              rows={6}
              maxLength={MAX_CHARACTERS_DESCRIPTION}
            />
          </Grid2>
          <Grid2 size={12}>
            <FormField
              type={FormFieldType.Text}
              label="Areas in or near the neighborhood to be avoided. If none, write 'None'"
              name="communitySection.areasToAvoid"
              control={control}
              multiline
              rows={6}
              maxLength={MAX_CHARACTERS_DESCRIPTION}
            />
          </Grid2>
        </Grid2>
      </Paper>

      <Paper sx={{ p: 2 }}>
        <Grid2 container spacing={2}>
          <Grid2 size={12}>
            <Typography variant="subtitle1">School Family Information</Typography>
          </Grid2>
          <Grid2 size={6}>
            <FormField
              control={control}
              name="schoolFamilyInfo.schoolTransport"
              type={FormFieldType.Checkboxset}
              options={schoolTransportOptions}
              label="How will the student get to school?"
            />
          </Grid2>
          <Grid2 size={6}>
            <FormField
              type={FormFieldType.Text}
              label="Approximate distance between school and home"
              name="schoolFamilyInfo.distanceToSchool"
              control={control}
              textSuffix="miles"
            />
          </Grid2>

          <Grid2 size={12}>
            <FormField
              type={FormFieldType.Radio}
              label="Would the family provide special transportation for activities after school?"
              name="schoolFamilyInfo.specialTransportation.value"
              control={control}
              options={yesNoOptions}
            />
          </Grid2>
          <Grid2 size={12}>
            <FormField
              type={FormFieldType.Text}
              label="If yes, then how often?"
              name="schoolFamilyInfo.specialTransportation.frequency"
              control={control}
              multiline
              rows={6}
              disabled={watchSpecialTransportation !== 'yes'}
              maxLength={MAX_CHARACTERS_DESCRIPTION}
            />
          </Grid2>
          <Grid2 size={12}>
            <FormField
              type={FormFieldType.Radio}
              label="Has any member of your family had contact with a coach regarding hosting an exchange student with particular athletic ability?"
              name="schoolFamilyInfo.contactWithCoach"
              control={control}
              options={yesNoOptions}
            />
          </Grid2>
          <Grid2 size={12}>
            <FormField
              type={FormFieldType.Radio}
              label="Does any member of your household coach, teach or work at the school the student will be attending?"
              name="schoolFamilyInfo.hostWorksAtSchool"
              control={control}
              options={yesNoOptions}
            />
          </Grid2>
          <Grid2 size={12}>
            <FormField
              type={FormFieldType.Text}
              label="Which, if any, of the family's children are enrolled in the same school?"
              name="schoolFamilyInfo.childrenSameSchool"
              control={control}
              multiline
              rows={6}
              maxLength={MAX_CHARACTERS_DESCRIPTION}
            />
          </Grid2>
          <Grid2 size={12}>
            <FormField
              type={FormFieldType.Text}
              label="What activities are these children involved in at school?"
              name="schoolFamilyInfo.childrenSchoolActivities"
              control={control}
              multiline
              rows={6}
              maxLength={MAX_CHARACTERS_DESCRIPTION}
            />
          </Grid2>
        </Grid2>
      </Paper>
    </Stack>
  );
};
